import {
  Box,
  Button,
  IconButton,
  Input,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import {HugeiconsIcon} from '@hugeicons/react';
import {Delete02Icon, PencilEdit01Icon, PlayIcon, Search01Icon, StopIcon} from '@hugeicons/core-free-icons';
import React, {useEffect, useState} from "react";
import {VocabularyRepository} from "../../../../repositories/VocabularyRepository";
import {toast} from "react-toastify";
import {grey} from "@mui/material/colors";
import {useDebounce} from "../../../../hooks/useDebounce";
import LoadingProgress from "../../../../components/loading-screen/LoadingProgress";
import {PhonemizeEditModal} from "./PhonemizeEditModal";

export const VocabularyTabContent = ({project, vocabularyType}) => {
  const [vocabulary, setVocabulary] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchPronunciation, setSearchPronunciation] = useState("");
  const [testPhrase, setTestPhrase] = useState("");
  const [testPronunciation, setTestPronunciation] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [phonemizerEditModalOpen, setPhonemizeEditModalOpen] = useState(false);
  const [phonemizerEditItem, setPhonemizeEditItem] = useState(null);
  const [currentAudio, setCurrentAudio] = useState(null);
  const debouncedSearchText = useDebounce(searchText, 500);
  const debouncedSearchPronunciation = useDebounce(searchPronunciation, 500);

  useEffect(() => {
    if (project?.id) fetchVocabulary();
  }, [project?.id, vocabularyType]);

  const fetchVocabulary = async () => {
    try {
      setLoading(true);
      const response = await VocabularyRepository.getVocabularyByType(project.id, vocabularyType);
      setVocabulary(response?.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch vocabulary");
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = async (phrase) => {
    if (loading) return;
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    setLoading(true);
    try {
      const response = await VocabularyRepository.getAudio(phrase, project?.id);
      const objectUrl = URL.createObjectURL(response.data);
      const audio = new Audio(objectUrl);
      audio.onended = () => {
        URL.revokeObjectURL(objectUrl);
        setCurrentAudio(null);
      };
      audio.play();
      setCurrentAudio(audio);
    } catch (error) {
      console.error(error);
      toast.error("Failed to play audio");
    } finally {
      setLoading(false);
    }
  };

  const handleStop = () => {
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      await VocabularyRepository.delete(id);
      toast.success("Successfully deleted");
      fetchVocabulary();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete!");
    }
  };

  const handleCreate = async () => {
    try {
      if (!testPhrase || !testPronunciation) {
        toast.error("Both phrase and pronunciation are required");
        return;
      }
      await VocabularyRepository.create({
        phrase: testPhrase,
        pronunciation: testPronunciation,
        type: vocabularyType,
        flowUid: project?.id,
      });
      toast.success("Vocabulary item created successfully");
      setTestPhrase("");
      setTestPronunciation("");
      fetchVocabulary();
    } catch (error) {
      console.error(error);
      toast.error("Failed to create vocabulary item");
    }
  };

  const handleUpdate = async () => {
    try {
      if (!editingItem || !testPhrase || !testPronunciation) return;
      await VocabularyRepository.update(editingItem.id, {
        phrase: testPhrase,
        pronunciation: testPronunciation,
        type: vocabularyType,
        flowUid: project?.id,
      });
      toast.success("Vocabulary item updated successfully");
      setTestPhrase("");
      setTestPronunciation("");
      setEditingItem(null);
      fetchVocabulary();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update vocabulary item");
    }
  };

  const handleCancel = () => {
    setTestPhrase("");
    setTestPronunciation("");
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    if (vocabularyType === "PHONEMIZER") {
      setPhonemizeEditItem(item);
      setPhonemizeEditModalOpen(true);
    } else {
      setTestPhrase(item.phrase);
      setTestPronunciation(item.pronunciation);
      setEditingItem(item);
    }
  };

  const filtered = vocabulary
    .filter(item => item?.phrase?.toLowerCase().includes(debouncedSearchText.toLowerCase()))
    .filter(item => item?.pronunciation?.toLowerCase().includes(debouncedSearchPronunciation.toLowerCase()));

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Stack direction="column" gap={3} sx={{flex: 1, overflow: "hidden", pt: 2, pb: 5}}>
      {loading && <LoadingProgress/>}

      <Stack direction="row" gap={2}>
        <TextField
          label="Phrase"
          placeholder="Phrase"
          disabled={loading}
          value={testPhrase}
          onChange={e => setTestPhrase(e.target.value)}
          sx={{minWidth: "280px", flex: 1}}
          multiline
          maxRows={3}
          InputLabelProps={{shrink: true}}
          InputProps={{
            endAdornment: (
              <IconButton
                disabled={loading || !testPhrase}
                onClick={() => currentAudio === null ? handlePlay(testPhrase) : handleStop()}
              >
                            {currentAudio === null ? <HugeiconsIcon icon={PlayIcon} strokeWidth={2}/> : <HugeiconsIcon icon={StopIcon}/>}
              </IconButton>
            )
          }}
        />
        <TextField
          label="Pronunciation"
          placeholder="Pronunciation"
          disabled={loading}
          value={testPronunciation}
          onChange={e => setTestPronunciation(e.target.value)}
          sx={{minWidth: "280px", flex: 1}}
          multiline
          maxRows={3}
          InputLabelProps={{shrink: true}}
          InputProps={{
            endAdornment: (
              <IconButton
                disabled={loading || !testPronunciation}
                onClick={() => currentAudio === null ? handlePlay(testPronunciation) : handleStop()}
              >
                          {currentAudio === null ? <HugeiconsIcon icon={PlayIcon} strokeWidth={2}/> : <HugeiconsIcon icon={StopIcon}/>}
              </IconButton>
            )
          }}
        />
        {vocabularyType !== "PHONEMIZER" && (
          <Box sx={{display: "flex", flexDirection: "row", gap: 1, alignItems: "flex-start", pt: 1}}>
            <Button
              color="secondary"
              variant="ghost"
              className="border-shadow"
              sx={{bgcolor: "transparent", fontWeight: 600, px: 3, textWrap: "nowrap", borderRadius: "8px !important", "&:hover": {bgcolor: grey[100]}}}
              onClick={handleCancel}
            >
              {editingItem ? "Cancel" : "Reset"}
            </Button>
            <Button
              color="secondary"
              variant="ghost"
              className="border-shadow"
              sx={{bgcolor: grey[100], fontWeight: 600, px: 3, textWrap: "nowrap", borderRadius: "8px !important", "&:hover": {bgcolor: grey[300]}}}
              onClick={editingItem ? handleUpdate : handleCreate}
              disabled={!testPhrase || !testPronunciation}
            >
              {editingItem ? "Update" : "Create"}
            </Button>
          </Box>
        )}
      </Stack>

      <Box sx={{flex: 1, overflow: "auto"}}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Input
                  placeholder="Search phrase..."
                  sx={{minWidth: "200px"}}
                  startAdornment={<HugeiconsIcon icon={Search01Icon} style={{marginRight: 10}} size={18}/>} 
                  value={searchText}
                  onChange={e => {setSearchText(e.target.value); setPage(0);}}
                  disableUnderline
                  fullWidth
                />
              </TableCell>
              <TableCell padding="checkbox">
                <Input
                  placeholder="Search pronunciation..."
                  sx={{minWidth: "200px"}}
                  startAdornment={<HugeiconsIcon icon={Search01Icon} style={{marginRight: 10}} size={18}/>} 
                  value={searchPronunciation}
                  onChange={e => {setSearchPronunciation(e.target.value); setPage(0);}}
                  disableUnderline
                  fullWidth
                />
              </TableCell>
              <TableCell/>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">No vocabulary items found</TableCell>
              </TableRow>
            ) : (
              paginated.map((item) => (
                <TableRow key={item.id}>
                  <TableCell padding="checkbox" sx={{px: 0.5}}>{item.phrase}</TableCell>
                  <TableCell padding="checkbox" sx={{px: 0.5}}>{item.pronunciation}</TableCell>
                  <TableCell padding="checkbox" align="right">
                    <Stack direction="row" gap={1} justifyContent="flex-end" sx={{p: 0.5}}>
                      <IconButton size="small" onClick={() => handlePlay(item.pronunciation)}>
                          <HugeiconsIcon icon={PlayIcon} size={18} strokeWidth={2}/>
                      </IconButton>
                      <IconButton size="small" onClick={() => handleEdit(item)}>
                        <HugeiconsIcon icon={PencilEdit01Icon} size={18}/>
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                        <HugeiconsIcon icon={Delete02Icon} size={18}/>
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      <Stack direction="row" justifyContent="flex-end">
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={e => {setRowsPerPage(parseInt(e.target.value, 10)); setPage(0);}}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Stack>

      <PhonemizeEditModal
        open={phonemizerEditModalOpen}
        onClose={() => {setPhonemizeEditModalOpen(false); setPhonemizeEditItem(null);}}
        onSuccess={() => fetchVocabulary()}
        project={project}
        item={phonemizerEditItem}
        vocabularyType={vocabularyType}
      />
    </Stack>
  );
};
